/* eslint-disable react/prop-types, no-unused-vars */
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';

const TableMap = lazy(() => import('./pages/Restaurant/TableMap'));
const WaiterPOS = lazy(() => import('./pages/Restaurant/WaiterPOS'));
const KitchenView = lazy(() => import('./pages/Restaurant/KitchenView'));
const RoomDashboard = lazy(() => import('./pages/Hotel/RoomDashboard'));
const InventoryView = lazy(() => import('./pages/Admin/InventoryView'));
const IngredientsView = lazy(() => import('./pages/Admin/IngredientsView'));
const ReportsView = lazy(() => import('./pages/Admin/ReportsView'));
const EventsDashboard = lazy(() => import('./pages/Admin/EventsDashboard'));
const EventDetails = lazy(() => import('./pages/Admin/EventDetails'));
const CashierView = lazy(() => import('./pages/Restaurant/CashierView'));
const Login = lazy(() => import('./pages/Login'));
const UsersView = lazy(() => import('./pages/Admin/UsersView'));
const TablesView = lazy(() => import('./pages/Admin/TablesView'));
const RoomsView = lazy(() => import('./pages/Admin/RoomsView'));
const HistoricalEntryView = lazy(() => import('./pages/Admin/HistoricalEntryView'));
const AuditDashboard = lazy(() => import('./pages/Admin/AuditDashboard'));
const AdminExpenseControl = lazy(() => import('./pages/Admin/AdminExpenseControl'));
import { Toaster } from 'react-hot-toast';
import { LayoutDashboard, Utensils, ChefHat, Hotel, Package, BarChart3, DollarSign, PartyPopper, Users, MapPin, BedDouble, Sun, Moon, History, ShieldAlert } from 'lucide-react';
import api from './api/axios';
import Layout from './components/ui/Layout';

function ProtectedRoute({ allowedRoles, currentUser, children }) {
  if (!allowedRoles.includes(currentUser.role) && currentUser.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }
  return children;
}

function RoleBasedHome({ currentUser }) {
  if (currentUser.role === 'CASHIER') return <Navigate to="/caja" replace />;
  if (currentUser.role === 'RECEPTIONIST') return <Navigate to="/hotel" replace />;
  if (currentUser.role === 'WAITER') return <Navigate to="/restaurant" replace />;
  if (currentUser.role === 'KITCHEN') return <Navigate to="/kitchen" replace />;
  
  return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-center animate-slide-up">
      <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-primary to-secondary flex items-center justify-center shadow-[0_0_40px_rgba(77,124,15,0.3)] dark:shadow-[0_0_40px_rgba(139,92,246,0.4)] mb-8">
        <LayoutDashboard className="text-primary-foreground" size={40} />
      </div>
      <h2 className="text-5xl font-extrabold mb-6 tracking-tight text-glow text-foreground">Sistema de Gestión Integrado</h2>
      <p className="text-xl text-slate-600 dark:text-slate-600 dark:text-slate-300 max-w-2xl mb-10 leading-relaxed">
        Administra tu hotel y restaurante desde una única plataforma moderna, rápida y eficiente.
      </p>
      <div className="flex flex-wrap justify-center gap-6">
        <Link to="/restaurant" className="glass-button px-6 py-4 text-lg font-semibold text-foreground flex items-center space-x-2">
          <Utensils size={24} className="text-primary" />
          <span>Ir a Restaurante</span>
        </Link>
        <Link to="/hotel" className="glass-button px-6 py-4 text-lg font-semibold text-foreground flex items-center space-x-2">
          <Hotel size={24} className="text-blue-500" />
          <span>Ir a Hotel</span>
        </Link>
        <Link to="/inventory" className="glass-button px-6 py-4 text-lg font-semibold text-foreground flex items-center space-x-2">
          <Package size={24} className="text-purple-500" />
          <span>Productos</span>
        </Link>
        <Link to="/ingredients" className="glass-button px-6 py-4 text-lg font-semibold text-foreground flex items-center space-x-2">
          <Package size={24} className="text-pink-500" />
          <span>Kardex</span>
        </Link>
        <Link to="/reports" className="glass-button px-6 py-4 text-lg font-semibold text-foreground flex items-center space-x-2">
          <BarChart3 size={24} className="text-cyan-500" />
          <span>Reportes</span>
        </Link>
        <Link to="/historico" className="glass-button px-6 py-4 text-lg font-semibold text-foreground flex items-center space-x-2">
          <History size={24} className="text-amber-500" />
          <span>Carga Histórica</span>
        </Link>
        <Link to="/audit" className="glass-button px-6 py-4 text-lg font-semibold text-foreground flex items-center space-x-2">
          <ShieldAlert size={24} className="text-rose-500" />
          <span>Auditoría</span>
        </Link>
      </div>
    </div>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const saved = localStorage.getItem('currentUser');
      return saved && saved !== 'undefined' ? JSON.parse(saved) : null;
    } catch (e) {
      localStorage.removeItem('currentUser');
      return null;
    }
  });

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark'; // Oscuro por defecto
  });

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  if (!currentUser) {
    return (
      <>
        <Toaster position="top-right" toastOptions={{ style: { background: 'rgba(15, 23, 42, 0.8)', color: '#fff' } }} />
        <Suspense fallback={<div className="flex items-center justify-center h-screen bg-background text-foreground">Cargando...</div>}>
          <Login onLogin={setCurrentUser} />
        </Suspense>
      </>
    );
  }

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      console.error(e);
    }
    localStorage.removeItem('currentUser');
    setCurrentUser(null);
  };

  return (
    <Router>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: 'var(--background)',
            color: 'var(--foreground)',
            backdropFilter: 'blur(10px)',
            border: '1px solid var(--border)',
          }
        }} 
      />
      <Layout currentUser={currentUser} theme={theme} toggleTheme={toggleTheme} handleLogout={handleLogout}>
        <Suspense fallback={<div className="flex items-center justify-center h-[50vh] text-primary">Cargando módulos...</div>}>
          <Routes>
            <Route path="/" element={<RoleBasedHome currentUser={currentUser} />} />
            
            {/* Restaurant & Waiter POS */}
            <Route path="/restaurant" element={<ProtectedRoute allowedRoles={['WAITER']} currentUser={currentUser}><TableMap /></ProtectedRoute>} />
            <Route path="/restaurant/pos/:tableId" element={<ProtectedRoute allowedRoles={['WAITER']} currentUser={currentUser}><WaiterPOS /></ProtectedRoute>} />
            
            {/* Kitchen */}
            <Route path="/kitchen" element={<ProtectedRoute allowedRoles={['KITCHEN']} currentUser={currentUser}><KitchenView /></ProtectedRoute>} />
            
            {/* Cashier */}
            <Route path="/caja" element={<ProtectedRoute allowedRoles={['CASHIER']} currentUser={currentUser}><CashierView /></ProtectedRoute>} />
            
            {/* Hotel */}
            <Route path="/hotel" element={<ProtectedRoute allowedRoles={['RECEPTIONIST']} currentUser={currentUser}><RoomDashboard /></ProtectedRoute>} />
            
            {/* Admin Only routes */}
            <Route path="/inventory" element={<ProtectedRoute allowedRoles={[]} currentUser={currentUser}><InventoryView /></ProtectedRoute>} />
            <Route path="/ingredients" element={<ProtectedRoute allowedRoles={[]} currentUser={currentUser}><IngredientsView /></ProtectedRoute>} />
            <Route path="/tables" element={<ProtectedRoute allowedRoles={[]} currentUser={currentUser}><TablesView /></ProtectedRoute>} />
            <Route path="/rooms" element={<ProtectedRoute allowedRoles={[]} currentUser={currentUser}><RoomsView /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute allowedRoles={[]} currentUser={currentUser}><UsersView /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute allowedRoles={[]} currentUser={currentUser}><ReportsView /></ProtectedRoute>} />
            <Route path="/historico" element={<ProtectedRoute allowedRoles={['ADMIN']} currentUser={currentUser}><HistoricalEntryView /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute allowedRoles={['ADMIN']} currentUser={currentUser}><AdminExpenseControl /></ProtectedRoute>} />
            <Route path="/audit" element={<ProtectedRoute allowedRoles={[]} currentUser={currentUser}><AuditDashboard /></ProtectedRoute>} />
            
            {/* Mixed */}
            <Route path="/events" element={<ProtectedRoute allowedRoles={['WAITER']} currentUser={currentUser}><EventsDashboard /></ProtectedRoute>} />
            <Route path="/events/:id" element={<ProtectedRoute allowedRoles={['WAITER']} currentUser={currentUser}><EventDetails /></ProtectedRoute>} />
          </Routes>
        </Suspense>
      </Layout>
    </Router>
  );
}

export default App;
